/**
 * System Check Step Component
 *
 * Shows system capabilities (GPU, RAM, Disk, OS) with recommendations.
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { SystemCheckResult } from '@/services/setupService';
import { getStatusBadge } from '@/services/setupService';

interface SystemCheckStepProps {
  systemCheck: SystemCheckResult | null;
  isLoading: boolean;
  gpuCalibrationRetryCount: number;
  maxGpuCalibrationRetries: number;
  onNext: () => void;
  onBack: () => void;
  onRecheck: () => void;
}

export function SystemCheckStep({
  systemCheck,
  isLoading,
  gpuCalibrationRetryCount,
  maxGpuCalibrationRetries,
  onNext,
  onBack,
  onRecheck,
}: SystemCheckStepProps) {
  const { t } = useTranslation();

  if (isLoading) {
    // gpuCalibrationRetryCount > 0 means this load is an automatic
    // GPU-calibration retry, not the initial hardware scan - the initial
    // scan is quick, but a calibration (re-)run can take a couple of
    // minutes, so say so instead of the generic "just a moment" copy.
    const isCalibrating = gpuCalibrationRetryCount > 0;
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-secondary-900 dark:text-secondary-100 mb-2">
          {isCalibrating
            ? t('wizard.systemCheck.calibrating', 'Calibrating GPU')
            : t('wizard.systemCheck.checking', 'Checking Your System')}
        </h2>
        <p className="text-secondary-600 dark:text-secondary-400">
          {isCalibrating
            ? t(
                'wizard.systemCheck.calibratingWait',
                'Attempt {{attempt}} of {{max}} - this can take a couple of minutes...',
                { attempt: gpuCalibrationRetryCount + 1, max: maxGpuCalibrationRetries + 1 }
              )
            : t('wizard.systemCheck.pleaseWait', 'This will only take a moment...')}
        </p>
      </div>
    );
  }

  if (!systemCheck) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-secondary-900 dark:text-secondary-100 mb-2">
          {t('wizard.systemCheck.failed', 'System Check Failed')}
        </h2>
        <button
          onClick={onRecheck}
          className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
        >
          {t('wizard.systemCheck.retry', 'Try Again')}
        </button>
      </div>
    );
  }

  const statusBadge = getStatusBadge(systemCheck.overallStatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-secondary-100 mb-2">
          {t('wizard.systemCheck.title', 'System Check')}
        </h2>
        <p className="text-secondary-600 dark:text-secondary-400">
          {t('wizard.systemCheck.subtitle', "Here's what we found on your system")}
        </p>
      </div>

      {/* Overall status badge */}
      <div className="flex justify-center">
        <span
          className={`
            px-4 py-2 rounded-full text-sm font-medium
            ${statusBadge.variant === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : ''}
            ${statusBadge.variant === 'info' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : ''}
            ${statusBadge.variant === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' : ''}
            ${statusBadge.variant === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : ''}
          `}
        >
          {systemCheck.canRunAllLocally
            ? t('wizard.systemCheck.canRunAll', 'Your system can run all AI tools locally!')
            : t('wizard.systemCheck.limitedCapabilities', 'Limited local capabilities')}
        </span>
      </div>

      {/* System specs grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* GPU */}
        <SystemCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
              />
            </svg>
          }
          title={t('wizard.systemCheck.gpu', 'GPU')}
          value={systemCheck.gpu.name}
          detail={
            systemCheck.gpu.isAvailable
              ? `${systemCheck.gpu.vramGb.toFixed(1)} GB VRAM`
              : t('wizard.systemCheck.noGpu', 'No GPU detected')
          }
          status={systemCheck.gpu.isSupportedForAi ? 'success' : 'warning'}
          recommendation={systemCheck.gpu.recommendation}
        />

        {/* RAM */}
        <SystemCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          }
          title={t('wizard.systemCheck.ram', 'RAM')}
          value={`${systemCheck.ram.totalGb.toFixed(0)} GB`}
          detail={`${systemCheck.ram.availableGb.toFixed(1)} GB ${t('wizard.systemCheck.available', 'available')}`}
          status={systemCheck.ram.isSufficient ? 'success' : 'warning'}
          recommendation={systemCheck.ram.recommendation}
        />

        {/* Disk */}
        <SystemCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
              />
            </svg>
          }
          title={t('wizard.systemCheck.storage', 'Storage')}
          value={`${systemCheck.disk.freeGb.toFixed(0)} GB ${t('wizard.systemCheck.free', 'free')}`}
          detail={systemCheck.disk.diskType.toUpperCase()}
          status={systemCheck.disk.isSufficient ? 'success' : 'warning'}
          recommendation={systemCheck.disk.recommendation}
        />

        {/* OS */}
        <SystemCard
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          }
          title={t('wizard.systemCheck.os', 'Operating System')}
          value={systemCheck.os.name}
          detail={systemCheck.os.architecture}
          status={systemCheck.os.isCompatible ? 'success' : 'warning'}
          recommendation={systemCheck.os.recommendation}
        />
      </div>

      {/* Recommended components */}
      {systemCheck.recommendedComponents.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
          <h3 className="font-medium text-green-800 dark:text-green-200 mb-2">
            {t('wizard.systemCheck.recommended', 'Recommended for your system:')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {systemCheck.recommendedComponents.map((comp) => (
              <span
                key={comp}
                className="px-3 py-1 bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300 rounded-full text-sm"
              >
                {comp}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* GPU benchmark gate: when an NVIDIA GPU is present but the
          overlay_cuda benchmark cache wasn't produced (transient FFmpeg
          failure, no nvidia-smi, etc.), we MUST block "Continue". Shipping
          an uncalibrated install falls back to MAX_GPU_OVERLAY_COUNT=20,
          which is what introduced the chunk-split zoom regression the user
          reported. The wizard auto-retries a bounded number of times first
          (see useSetupWizard's GPU-calibration effect) so a single
          transient failure doesn't require user action; only a *persistent*
          failure surfaces this manual "Re-check" block. */}
      {!systemCheck.gpuBenchmarkReady && gpuCalibrationRetryCount >= maxGpuCalibrationRetries && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-xl p-4">
          <h3 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">
            {t('wizard.systemCheck.benchmarkRequired', 'GPU calibration required')}
          </h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            {t(
              'wizard.systemCheck.benchmarkRequiredBody',
              "We couldn't measure your GPU's safe overlay limit after {{attempts}} attempts. The app needs this calibration to avoid render crashes. Please click Re-check to try again — it takes 1–2 minutes.",
              { attempts: gpuCalibrationRetryCount + 1 }
            )}
          </p>
        </div>
      )}

      {!systemCheck.gpuBenchmarkReady && gpuCalibrationRetryCount < maxGpuCalibrationRetries && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {t(
              'wizard.systemCheck.benchmarkRetrying',
              "Couldn't measure your GPU's safe overlay limit on the first try - retrying automatically..."
            )}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <button
          onClick={onBack}
          className="px-4 py-2 text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200 min-h-[44px]"
        >
          {t('wizard.back', 'Back')}
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={onRecheck}
            className="px-4 py-2 text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200 text-sm"
          >
            {t('wizard.systemCheck.recheck', 'Re-check')}
          </button>
          <button
            onClick={onNext}
            disabled={!systemCheck.gpuBenchmarkReady}
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-secondary-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors min-h-[44px]"
          >
            {t('wizard.continue', 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface SystemCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  detail: string;
  status: 'success' | 'warning' | 'error';
  recommendation: string;
}

function SystemCard({ icon, title, value, detail, status, recommendation }: SystemCardProps) {
  const statusColors = {
    success: 'border-green-500 bg-green-50 dark:bg-green-900/20',
    warning: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    error: 'border-red-500 bg-red-50 dark:bg-red-900/20',
  };

  const iconColors = {
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    error: 'text-red-600 dark:text-red-400',
  };

  return (
    <div className={`p-4 rounded-xl border-l-4 ${statusColors[status]}`}>
      <div className="flex items-start gap-3">
        <div className={iconColors[status]}>{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-secondary-500 dark:text-secondary-400">{title}</div>
          <div className="font-semibold text-secondary-900 dark:text-secondary-100 truncate">
            {value}
          </div>
          <div className="text-sm text-secondary-600 dark:text-secondary-400">{detail}</div>
          <div className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
            {recommendation}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SystemCheckStep;
